import React, { useEffect, useState } from "react";
import { FaUserAlt } from "react-icons/fa";
import GlobalRatingBadge from "../components/GlobalRatingBadge";
import { getProviderProductComments } from "../services/providerProductService";
import { elakayAutoText } from "../../pageInside/dataElakay";
import "../../pageInside/commentaire/UserComment.css";
import "./ProviderProductReviews.css";

const STARS = [5, 4, 3, 2, 1];
const formatDate = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString();
};

// Provider Product View -- Ratings & Comments (ticket §4/§5): reads the SAME global
// ratings/comments the server already aggregated across every Seller Product linked to this
// Provider Product (server_elakay/dropshippingInterne/services/providerProductRatings.js --
// real per-buyer votes/comments, never invented here). Reuses UserComment.css's visual language
// (comment-item/comment-bubble/user-avatar/comment-text) for consistency with the existing Seller/
// User Royale comment design, but is intentionally read-only here: each comment in this aggregated
// view belongs to a DIFFERENT Seller's own Product listing, so Like/Dislike (which writes to one
// specific product's comment) is left to that Seller's own Product View, not duplicated here.
export default function ProviderProductReviews({ providerProductId, token, ratings }) {
  const [comments, setComments] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const limit = 10;

  const load = (nextSkip) => {
    setLoading(true); setError("");
    getProviderProductComments(token, providerProductId, { skip: nextSkip, limit }).then((r) => {
      if (r?.error) { setError(r.error); setLoading(false); return; }
      setComments((prev) => (nextSkip === 0 ? r.items : [...prev, ...r.items]));
      setTotal(r.total || 0);
      setSkip(nextSkip);
      setLoading(false);
    });
  };
  useEffect(() => { load(0); }, [providerProductId]); // eslint-disable-line react-hooks/exhaustive-deps

  const distribution = ratings?.ratingDistribution || {};
  const totalRatings = Number(ratings?.totalRatings) || 0;

  return (
    <div className="ppr">
      <div className="ppr__summary">
        <GlobalRatingBadge ratings={ratings} size="20px" />
        {totalRatings > 0 && (
          <div className="ppr__distribution" data-testid="rating-distribution">
            {STARS.map((star) => {
              const count = Number(distribution[star]) || 0;
              const pct = totalRatings > 0 ? Math.round((count / totalRatings) * 100) : 0;
              return (
                <div key={star} className="ppr__dist-row">
                  <span className="ppr__dist-label">{star} {elakayAutoText("h_ppr_stars", "stars")}</span>
                  <span className="ppr__dist-bar"><span className="ppr__dist-fill" style={{ width: `${pct}%` }} /></span>
                  <span className="ppr__dist-count">{count}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="user-comments-section">
        {error && <p className="ppr__error" role="alert">{error}</p>}
        {!error && !loading && comments.length === 0 && (
          <p className="ppr__empty">{elakayAutoText("h_ppr_no_comments", "No comments yet.")}</p>
        )}
        <div className="comments-list">
          {comments.map((comment) => (
            <div key={comment.commentId} className="comment-item comment-other">
              <div className="comment-bubble">
                <div className="comment-header">
                  <div className="user-info">
                    <div className="user-avatar"><FaUserAlt /></div>
                    <div className="user-details">
                      <span className="user-name">{comment.name}</span>
                      {comment.shopName && <span className="ppr__shop">{elakayAutoText("h_ppr_via_shop", "via {value0}", { value0: comment.shopName })}</span>}
                    </div>
                  </div>
                  {comment.createdAt && <span className="ppr__date">{formatDate(comment.createdAt)}</span>}
                </div>
                <div className="comment-content">
                  <p className="comment-text">{comment.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        {loading && <p className="ppr__loading" role="status">{elakayAutoText("h_ppr_loading", "Loading…")}</p>}
        {!loading && !error && comments.length < total && (
          <button type="button" className="ppr__load-more" onClick={() => load(skip + limit)}>
            {elakayAutoText("h_ppr_load_more", "Load more comments")}
          </button>
        )}
      </div>
    </div>
  );
}
